/* eslint-disable no-param-reassign */
import { message } from 'antd';
import React, { useCallback, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/Auth';
import AnnouncementProvider from '../providers/announcement';
import api from './api';

export const WithAxios: React.FC = () => {
    const { logOut, resetAssumedToken, isAuth } = useAuth();
    const navigate = useNavigate();
    const requestInterceptorRef = useRef<number | null>(null);
    const responseInterceptorRef = useRef<number | null>(null);
    const isHandlingSessionExpiredRef = useRef(false);
    const logOutRef = useRef(logOut);
    const resetAssumedTokenRef = useRef(resetAssumedToken);
    const navigateRef = useRef(navigate);

    useEffect(() => {
        logOutRef.current = logOut;
        resetAssumedTokenRef.current = resetAssumedToken;
    }, [logOut, resetAssumedToken]);

    useEffect(() => {
        navigateRef.current = navigate;
    }, [navigate]);

    const apiUpdate = useCallback(() => {
        if (requestInterceptorRef.current !== null) {
            api.interceptors.request.eject(requestInterceptorRef.current);
        }
        if (responseInterceptorRef.current !== null) {
            api.interceptors.response.eject(responseInterceptorRef.current);
        }

        // when will make a request, by default, put the avaiable token on local storage to request bearer
        requestInterceptorRef.current = api.interceptors.request.use(
            (config: { headers: { common: { Authorization: string } } }) => {
                config.headers.common.Authorization = `Bearer ${
                    localStorage.getItem('@NeerHouse:assumedToken') ??
                    localStorage.getItem('@NeerHouse:token')
                }`;

                return config;
            },
            (error: any) => {
                return Promise.reject(error);
            },
        );

        // when response status code is 401 (unathorized), the interceptor make an auto logout
        responseInterceptorRef.current = api.interceptors.response.use(
            (response: any) => {
                return response;
            },
            async (error: any) => {
                const responseData = error?.response?.data;
                const responseError =
                    responseData && typeof responseData === 'object'
                        ? responseData.error
                        : undefined;
                const responseMessage =
                    typeof responseData === 'string'
                        ? responseData
                        : responseData?.message;
                const isBlockedAccount =
                    responseError ===
                        'this account need be unlocked to make this action' ||
                    responseError === 'bloqued-account';

                if (error?.message === 'Request Cancelada') {
                    return Promise.reject(new Error('Request Cancelada'));
                }
                if (error?.response?.status === 403) {
                    if (isBlockedAccount) {
                        if (isHandlingSessionExpiredRef.current) {
                            return Promise.reject(error);
                        }
                        isHandlingSessionExpiredRef.current = true;

                        await logOutRef.current();
                        navigateRef.current('/login');
                        return Promise.reject(error);
                    }
                }
                if (error?.response?.status === 401) {
                    if (responseError === 'read-only-permission') {
                        message.error(
                            responseMessage ||
                                'Você não possui permissão para realizar esta ação',
                        );
                        return Promise.reject(error);
                    }

                    if (error?.config?.skipAuthLogout) {
                        return Promise.reject(error);
                    }

                    if (isHandlingSessionExpiredRef.current) {
                        return Promise.reject(error);
                    }
                    isHandlingSessionExpiredRef.current = true;

                    if (localStorage.getItem('@NeerHouse:assumedToken')) {
                        resetAssumedTokenRef.current();
                    }
                    message.info({
                        key: 'session-expired',
                        content:
                            responseMessage ||
                            'Sessão atual expirou, por favor, entre novamente',
                    });
                    await logOutRef.current();
                    navigateRef.current('/login');

                    return Promise.reject(error);
                }
                return Promise.reject(error);
            },
        );
    }, []);

    useEffect(() => {
        apiUpdate();

        return () => {
            if (requestInterceptorRef.current !== null) {
                api.interceptors.request.eject(requestInterceptorRef.current);
                requestInterceptorRef.current = null;
            }
            if (responseInterceptorRef.current !== null) {
                api.interceptors.response.eject(responseInterceptorRef.current);
                responseInterceptorRef.current = null;
            }
        };
    }, [apiUpdate]);

    useEffect(() => {
        if (isAuth) {
            isHandlingSessionExpiredRef.current = false;
        }
    }, [isAuth]);

    return (
        <AnnouncementProvider>
            <Outlet />
        </AnnouncementProvider>
    );
};
